
<template>
	<bg-modal
		:value="isShown"
		title="Riwayat Perubahan Data Penyewa"
		desktop-size="lg"
		:body-scroll="false"
		:close-on-click-backdrop="false"
		class="tenant-contact-edit-changelog-modal"
		@close="$emit('close')"
	>
		<div
			v-if="isFetchingChangelogs"
			class="tenant-contact-edit-changelog-modal__loading"
		>
			<bg-loading-spinner size="xl" color="#a4237e" />
		</div>

		<template v-else-if="changelogs.length > 0">
			<bg-table
				:rows="changelogs"
				:columns="COLUMNS"
				data-testid="changelog-table"
			>
				<template #column-attachments="{ row }">
					<div
						:class="{
							'attachments': true,
							'attachments--multi': row.attachments.length > 1
						}"
					>
						<div
							v-for="attachment in row.attachments"
							:key="attachment.id"
							class="attachments__item"
						>
							<bg-image
								:src="attachment.urls.small"
								ratio="1:1"
								class="cursor-pointer"
								@click.native="openAttachment(
									attachment.urls.large
								)"
							/>
						</div>
					</div>
				</template>
			</bg-table>

			<bg-pagination
				v-model="pagination.page"
				:page-total="pagination.pageTotal"
				class="bg-u-mt-xl"
				@click="fetchChangelogs"
			/>
		</template>

		<div v-else class="tenant-contact-edit-changelog-modal__empty-state">
			<bg-text>Data Tidak Ditemukan</bg-text>

			<bg-text size="body-2">
				Riwayat perubahan data penyewa akan tampil di sini.
			</bg-text>
		</div>
	</bg-modal>
</template>

<script>
import { getCurrentInstance, ref, computed } from '@vue/composition-api';
import { BgModal, BgTable, BgPagination, BgText, BgImage, BgLoadingSpinner } from 'bangul-vue';

import tenantApi from '@admin/api/endpoints/mamipay/tenant';
import { dateFormatterToDisplay } from 'Utils/formatter';

const COLUMNS = [
	{
		label: 'Pengubah',
		key: 'modified_by',
	},
	{
		label: 'Divisi',
		key: 'division',
	},
	{
		label: 'Perubahan Data',
		key: 'field_name',
	},
	{
		label: 'Data Lama',
		key: 'old_value',
	},
	{
		label: 'Data Baru',
		key: 'new_value',
	},
	{
		label: 'Diubah Pada',
		key: 'modified_at',
	},
	{
		label: 'Lampiran',
		key: 'attachments',
	},
];

export default {
	name: 'TenantContactEditChangelogModal',

	components: {
		BgModal,
		BgTable,
		BgPagination,
		BgText,
		BgImage,
		BgLoadingSpinner,
	},
	
	props: {
		isShown: {
			type: Boolean,
			default: false
		},
	},

	watch: {
		isShown: {
			handler(value) {
				if (value) {
					this.fetchChangelogs();
				}
			},
			immediate: true,
		},
	},

	setup() {
		const instance = getCurrentInstance();
		const { $route, $toast, $error } = instance.proxy;

		const contractId = computed(() => $route.params.contractId);
		
		const changelogs = ref([]);
		const pagination = ref({
			page: 1,
			pageTotal: 1,
		});

		const isFetchingChangelogs = ref(false);

		const openAttachment = attachmentUrl => {
			window.open(attachmentUrl, '_blank');
		};

		const fetchChangelogs = async (pageTarget = 1) => {
			try {
				isFetchingChangelogs.value = true;

				const response = await tenantApi.getTenantEditChangelog(
					contractId.value,
					pageTarget
				);

				if (response && response.data) {
					const {
						current_page: page,
						total_page: pageTotal,
					} = response.data.pagination;

					pagination.value = { page, pageTotal };

					changelogs.value = response.data.changes.map(change => {
						const modifiedAt = dateFormatterToDisplay(
							change.modified_at,
							'DD/MM/YYYY HH:mm'
						);

						return {
							...change,
							modified_at: modifiedAt,
						}
					});
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Terjadi galat, silakan coba lagi.';

				$toast.show(errorMessage);
				$error.report(error);
			} finally {
				isFetchingChangelogs.value = false;
			}
		};

		return {
			COLUMNS,
			changelogs,
			pagination,
			openAttachment,
			isFetchingChangelogs,
			fetchChangelogs,
		};
	}
};
</script>

<style lang="scss" src="./TenantContactEditChangelogModal.scss" scoped />
